'use client';

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@coas/ui/dialog';
import { cn } from '@coas/ui/lib';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from 'trpc/react';
import type { RouterOutputs } from 'trpc/shared';
import { useInvalidateAll } from '~/features/Cache';
import { Spinner } from '~/shared/ui/spinner';

type PickerItem = RouterOutputs['selectionList']['listForPicker']['items'][number];

type Props = {
    products: ReadonlyArray<{ id: string }>;
    open: boolean;
    onOpenChange: (open: boolean, clear?: boolean) => void;
};

function MembershipPill({ membership }: { membership?: PickerItem['membership'] }) {
    if (!membership) return null;

    const { state, count, total } = membership;

    const tone =
        state === 'all'
            ? 'bg-neutral-950 text-white'
            : state === 'some'
              ? 'bg-neutral-200 text-neutral-900'
              : 'bg-neutral-100 text-neutral-700';

    const text = state === 'all' ? `Уже все (${total})` : state === 'some' ? `Часть (${count}/${total})` : 'Нет';

    return <span className={cn('shrink-0 rounded-8 px-2 py-1 text-11 font-600', tone)}>{text}</span>;
}

export const ProductSelectionListsDialog: React.FC<Props> = ({ products, open, onOpenChange }) => {
    const productIds = useMemo(() => products.map(p => p.id), [products]);

    const [onlyActive, setOnlyActive] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());

    const searchValue = useMemo(() => {
        const s = search.trim();
        return s.length > 0 ? s : undefined;
    }, [search]);

    const { mutate: invalidateAll } = useInvalidateAll();

    const { data, isLoading, isError, refetch } = api.selectionList.listForPicker.useQuery(
        { search: searchValue, onlyActive, productIds },
        { enabled: open, refetchOnWindowFocus: false },
    );

    const items = (data?.items ?? []) as PickerItem[];

    const { mutate, isPending } = api.selectionList.addProductsToSelectionListsMany.useMutation({
        onMutate: () => toast.loading('Добавляем товары в подборки...', { id: 'addToLists' }),
        onSuccess: res => {
            invalidateAll();
            onOpenChange(false, true);

            const created = res?.totals?.createdPairs ?? 0;
            const already = res?.totals?.alreadyHadPairs ?? 0;

            toast.success(`Готово: добавлено связей ${created}${already ? `, уже было ${already}` : ''}`, {
                id: 'addToLists',
            });
        },
        onError: () => toast.error('Не удалось добавить товары в подборки', { id: 'addToLists' }),
    });

    useEffect(() => {
        if (!open) return;
        setOnlyActive(true);
        setSearch('');
        setSelected(new Set());
    }, [open]);

    const blockCloseIfLoading = (e: Event) => {
        if (isPending) e.preventDefault();
    };

    const selectedIds = useMemo(() => Array.from(selected), [selected]);
    const canSubmit = selectedIds.length > 0 && productIds.length > 0 && !isPending;

    const toggle = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return (
        <Dialog
            open={open || isPending}
            onOpenChange={next => {
                if (isPending) return;
                onOpenChange(next);
            }}
        >
            <DialogContent
                className={cn(
                    'z-[100] w-[min(92vw,640px)] rounded-12 border border-neutral-200 bg-neutral-50 text-neutral-950 shadow-lg outline-none',
                    'max-h-[min(80vh,calc(var(--visual-viewport-height)-2rem))] overflow-hidden',
                    'p-0',
                    '[&>button]:right-4 [&>button]:top-4 md:[&>button]:right-6 md:[&>button]:top-6',
                    '[&>button]:z-10',
                )}
                onEscapeKeyDown={blockCloseIfLoading}
                onPointerDownOutside={blockCloseIfLoading}
                onInteractOutside={blockCloseIfLoading}
            >
                <div className='flex max-h-full flex-col gap-4 px-4 py-4 md:px-6 md:py-6'>
                    <DialogHeader className='text-left pr-10'>
                        <div className='flex flex-col gap-2'>
                            <DialogTitle className='text-18 font-600 text-neutral-950 md:text-22'>
                                Добавить в подборки
                            </DialogTitle>

                            <DialogDescription className='text-left text-18 leading-snug text-neutral-500'>
                                Выберите одну или несколько подборок для {productIds.length} товар(ов)
                            </DialogDescription>
                        </div>
                    </DialogHeader>

                    <div className='flex flex-col gap-3'>
                        <label className='flex flex-col gap-1 text-16'>
                            Поиск:
                            <input
                                value={search}
                                onChange={e => setSearch(e.currentTarget.value)}
                                placeholder='Название подборки...'
                                className={cn(
                                    'w-full rounded-8 border border-neutral-300 bg-white px-2 py-1 text-16 text-neutral-900',
                                    'outline-none ring-0 focus:border-neutral-800',
                                )}
                            />
                        </label>

                        <label className='flex items-center gap-2 text-14 text-neutral-700 select-none'>
                            <input
                                type='checkbox'
                                checked={onlyActive}
                                onChange={e => setOnlyActive(e.currentTarget.checked)}
                                className='size-4 accent-neutral-950'
                            />
                            Только активные
                        </label>
                    </div>

                    <div className={cn('flex-1 overflow-hidden rounded-12 border border-neutral-200 bg-white')}>
                        <div className='max-h-[min(44vh,420px)] overflow-auto p-2'>
                            {isLoading && <div className='p-3 text-14 text-neutral-500'>Загружаем подборки…</div>}

                            {isError && (
                                <div className='flex items-center justify-between gap-3 p-3'>
                                    <div className='text-14 text-red-600'>Не удалось загрузить подборки</div>
                                    <button
                                        type='button'
                                        onClick={() => refetch()}
                                        className={cn(
                                            'h-8 rounded-8 bg-neutral-950 px-3 text-13 font-600 text-white',
                                            'disabled:pointer-events-none disabled:opacity-60',
                                        )}
                                    >
                                        Повторить
                                    </button>
                                </div>
                            )}

                            {!isLoading && !isError && items.length === 0 && (
                                <div className='p-3 text-14 text-neutral-500'>Подборки не найдены</div>
                            )}

                            {!isLoading && !isError && items.length > 0 && (
                                <div className='flex flex-col gap-2'>
                                    {items.map(item => {
                                        const membership = item.membership;
                                        const disabled = membership?.state === 'all';
                                        const checked = selected.has(item.id);

                                        return (
                                            <button
                                                key={item.id}
                                                type='button'
                                                onClick={() => {
                                                    if (disabled) return;
                                                    toggle(item.id);
                                                }}
                                                className={cn(
                                                    'w-full text-left',
                                                    'rounded-10 border border-neutral-200 bg-white px-3 py-2',
                                                    'transition-colors hover:border-neutral-300',
                                                    'disabled:pointer-events-none',
                                                    disabled && 'opacity-60',
                                                )}
                                                aria-disabled={disabled}
                                            >
                                                <div className='flex items-start gap-3'>
                                                    <input
                                                        type='checkbox'
                                                        checked={checked}
                                                        readOnly
                                                        disabled={disabled}
                                                        className='mt-1 size-4 accent-neutral-950'
                                                    />

                                                    <div className='min-w-0 flex-1'>
                                                        <div className='flex items-center gap-2'>
                                                            <span className='truncate text-15 font-600 text-neutral-950'>
                                                                {item.title}
                                                            </span>

                                                            {item.bookmark && (
                                                                <span className='rounded-8 bg-neutral-950 px-2 py-0.5 text-11 font-700 text-white'>
                                                                    Закреп
                                                                </span>
                                                            )}

                                                            {!item.isActive && (
                                                                <span className='rounded-8 bg-neutral-200 px-2 py-0.5 text-11 font-600 text-neutral-700'>
                                                                    Неактивна
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div className='mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-12 text-neutral-500'>
                                                            <span>товаров: {item._count.productsInSelectionList}</span>
                                                        </div>
                                                    </div>

                                                    <MembershipPill membership={membership} />
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='flex items-center justify-between gap-3'>
                        <div className='text-12 text-neutral-500'>
                            Выбрано подборок: <span className='font-700 text-neutral-900'>{selectedIds.length}</span>
                        </div>

                        <DialogFooter className='p-0'>
                            <div className='flex flex-row items-center justify-end gap-2 sm:gap-3'>
                                <DialogClose asChild>
                                    <button
                                        type='button'
                                        onClick={() => onOpenChange(false)}
                                        disabled={isPending}
                                        className={cn(
                                            'h-9 px-3 text-13 md:px-4 md:text-14',
                                            'rounded-8 font-500 shrink-0',
                                            'bg-neutral-200 text-neutral-900',
                                            'transition-colors duration-150 hover:bg-neutral-300',
                                            'disabled:pointer-events-none disabled:opacity-60',
                                        )}
                                    >
                                        Отмена
                                    </button>
                                </DialogClose>

                                <button
                                    type='button'
                                    disabled={!canSubmit}
                                    onClick={() => {
                                        mutate({ productIds, selectionListIds: selectedIds });
                                    }}
                                    className={cn(
                                        'h-9 px-3 text-13 md:px-4 md:text-14',
                                        'rounded-8 font-700 shrink-0',
                                        'bg-neutral-950 text-white',
                                        'transition-opacity duration-150',
                                        'disabled:pointer-events-none disabled:opacity-60',
                                        isPending && 'animate-pulse',
                                    )}
                                >
                                    Добавить
                                </button>
                            </div>
                        </DialogFooter>
                    </div>
                </div>

                {isPending && (
                    <div className='absolute inset-0 z-50 flex items-center justify-center rounded-12 bg-neutral-50/70'>
                        <Spinner className='size-6' />
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
